/**
 * Performs a deep comparison between two values to determine if they are equivalent.
 *
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns True if the values are equivalent, false otherwise
 */
export function isDeepEqual(a: unknown, b: unknown): boolean {
  // Handle simple cases
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isDeepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a as object);
    const keysB = Object.keys(b as object);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
      if (!isDeepEqual((a as any)[key], (b as any)[key])) return false;
    }

    return true;
  }

  return false;
}

/**
 * Performs a shallow comparison between two objects to determine if they are equivalent.
 *
 * @param a - First object to compare
 * @param b - Second object to compare
 * @returns True if the objects are equivalent, false otherwise
 */
export function isShallowEqual(
  a: Record<string, unknown>,
  b: Record<string, unknown>
): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key) || a[key] !== b[key]) {
      return false;
    }
  }

  return true;
}
